import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useAuth } from '../context/AuthContext';
import { getQuestion, deleteQuestion } from '../api/faq';
import FAQQuestionDialog from '../components/faq/FAQQuestionDialog';
import FAQAnswerForm from '../components/faq/FAQAnswerForm';
import FAQAnswerList from '../components/faq/FAQAnswerList';
import { createMarkdownComponents } from '../components/common/MarkdownComponents';

const FAQDetail = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showAnswerForm, setShowAnswerForm] = useState(false);
  
  const isAuthor = user && question && question.author.id === user.id;
  const isAdmin = user && (user.is_staff || user.is_superuser);
  const canEdit = isAuthor || isAdmin;
  const canDelete = isAuthor || isAdmin;
  const isDarkMode = document.documentElement.classList.contains('dark');

  const fetchQuestion = async () => {
    try {
      const data = await getQuestion(questionId);
      setQuestion(data);
    } catch (err) {
      console.error('Error fetching question:', err);
      setError('Failed to load the question');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestion();
  }, [questionId]);

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleDelete = async () => {
    setDeleteDialogOpen(false);
    try {
      await deleteQuestion(questionId);
      navigate('/faq');
    } catch (err) {
      console.error('Error deleting question:', err);
      setError('Failed to delete the question');
    }
  };

  const handleQuestionUpdated = () => {
    setEditDialogOpen(false);
    fetchQuestion();
  };

  const handleAnswerAdded = () => {
    setShowAnswerForm(false);
    fetchQuestion();
  };

  const handleAnswerUpdated = () => {
    fetchQuestion();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex justify-center items-center min-h-[60vh]">
          <svg className="w-10 h-10 text-blue-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      </div>
    );
  }

  if (error || !question) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <button 
          onClick={() => navigate('/faq')}
          className="flex items-center mb-4 text-primary-600 dark:text-primary-400 hover:underline"
        >
          <svg className="w-5 h-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to FAQ
        </button>
        <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-200 rounded-md border border-red-200 dark:border-red-800">
          {error || 'Question not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <button 
        onClick={() => navigate('/faq')}
        className="flex items-center mb-4 text-blue-600 hover:underline"
      >
        <svg className="w-5 h-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to FAQ
      </button>
      
      <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md p-6 border border-ui-light-border dark:border-ui-dark-border mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
                {question.title}
              </h1>
              {question.is_pinned && (
                <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 13V7a4 4 0 018 0v6a4 4 0 01-8 0z" />
                  <path fillRule="evenodd" d="M6 3a2 2 0 00-2 2v1h10V5a2 2 0 00-2-2H6z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary mb-4">
              <span>By {question.author.full_name}</span>
              <span>•</span>
              <span>{format(new Date(question.created_at), 'MMM d, yyyy h:mm a')}</span>
              {question.updated_at !== question.created_at && (
                <>
                  <span>•</span>
                  <span>Updated {format(new Date(question.updated_at), 'MMM d, yyyy h:mm a')}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 mb-4">
              {question.is_answered && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  Answered
                </span>
              )}
              {question.roles.map(role => (
                <span 
                  key={role.id} 
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                >
                  {role.name}
                </span>
              ))}
            </div>
          </div>
          {canEdit && (
            <div className="flex space-x-2 mt-4 sm:mt-0">
              <button 
                onClick={handleEdit}
                className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                title="Edit Question"
              >
                <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4h6a2 2 0 012 2v6m-3 5l-7 7H4a2 2 0 01-2-2v-4l7-7" />
                </svg>
              </button>
              {canDelete && (
                <button 
                  onClick={() => setDeleteDialogOpen(true)}
                  className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                  title="Delete Question"
                >
                  <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
        
        <hr className="my-4 border-gray-300 dark:border-gray-600" />

        <div className="prose prose-sm sm:prose lg:prose-lg dark:prose-invert max-w-none">
          <ReactMarkdown
            components={createMarkdownComponents(isDarkMode)}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
          >
            {question.question}
          </ReactMarkdown>
        </div>
      </div>

      <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md p-6 border border-ui-light-border dark:border-ui-dark-border">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary">
            Answers ({question.answers.length})
          </h2>
          {isAuthenticated && (
            <button
              onClick={() => setShowAnswerForm(!showAnswerForm)}
              className="px-4 py-2 bg-primary-600 text-white dark:text-ui-dark-text-primary rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
            >
              {showAnswerForm ? 'Cancel' : 'Add Answer'}
            </button>
          )}
        </div>

        {showAnswerForm && (
          <div className="mb-6">
            <FAQAnswerForm 
              questionId={questionId}
              onAnswerAdded={handleAnswerAdded}
              onCancel={() => setShowAnswerForm(false)}
            />
          </div>
        )}

        <FAQAnswerList 
          answers={question.answers}
          questionAuthorId={question.author.id}
          onAnswerUpdated={handleAnswerUpdated}
        />
      </div>

      <FAQQuestionDialog 
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onQuestionUpdated={handleQuestionUpdated}
        question={question}
      />
      
      {deleteDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div 
            className="absolute inset-0 bg-black opacity-50"
            onClick={() => setDeleteDialogOpen(false)}
          ></div>
          <div className="bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Confirm Deletion</h3>
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Are you sure you want to delete this question? This action cannot be undone and will also delete all answers.
            </p>
            <div className="flex justify-end space-x-2">
              <button 
                onClick={() => setDeleteDialogOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button 
                onClick={handleDelete}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQDetail;